import { IconButton } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import ToggleOffIcon from '@material-ui/icons/ToggleOffOutlined'
import ToggleOnIcon from '@material-ui/icons/ToggleOnOutlined'
import { upperFirst } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  DomainPermission_DomainEnum,
  DomainPermission_VerbEnum,
  PolicyRule_ProrationKindEnum,
  PolicyRule_VehicleTypesEnum,
} from 'src/api/gen/api'
import { IPolicyRuleTripTaxation } from 'src/api/types/policy'
import { Can } from 'src/commons/permissions'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { DeleteRuleButton } from 'src/components/Button'
import { ProrationField } from 'src/components/fields/Proration/ProrationField'
import { SimpleTable } from 'src/components/Table'
import { FieldTitle } from 'src/pages/Policies/commons'

import { GeographiesField, NumberField, RuleNameField, VehicleTypesField } from '../fields'

type Row = [
  TRulesValidation,
  string, // ruleId
  string, // name
  PolicyRule_VehicleTypesEnum[], // vehicle_types
  string[], // geographies
  number, // rate_amount
  string, // rate_amount_type
  PolicyRule_ProrationKindEnum | undefined, // proration_kind
  Record<string, number> | undefined, // proration_areas_mapping
  string | undefined // proration_time_spent_report_area
]

const TripTaxationRule = (props: IBaseTableProps<IPolicyRuleTripTaxation>) => {
  const { t } = useTranslation()

  const HEADERS: string[] = [
    '',
    t('rule.name'),
    t('rule.count.vehicle_types'),
    t('rule.geographies'),
    '',
    '',
    t('headers.proration'),
  ]
  const amountTitles: { [key: string]: string } = {
    fee: t('fee'),
    subsidy: t('subsidy'),
  }

  const handleChangeRule =
    (ruleId: string, key?: keyof IPolicyRuleTripTaxation) =>
    (
      val:
        | IPolicyRuleTripTaxation[keyof IPolicyRuleTripTaxation]
        | {
            proration_areas_mapping: Record<string, number> | undefined
            proration_kind: PolicyRule_ProrationKindEnum | undefined
            proration_time_spent_report_area: string | undefined
          } // Needed because multiple setState called in the same function would run asynchronously,
      // hence reverting each other
    ): void => {
      const newRules: IPolicyRuleTripTaxation[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }

        if (typeof val === 'object' && 'proration_kind' in val) {
          return { ...rule, ...val }
        }

        if (key === undefined) {
          throw new TypeError('key cannot be undefined if val is not an object')
        }

        return { ...rule, [key]: val }
      })
      props.handleChange(newRules)
    }

  function handleRateAmountChange(ruleId: string, value: IPolicyRuleTripTaxation['rate_amount']) {
    if (value < 0) {
      value = 0
    }
    handleChangeRule(ruleId, 'rate_amount')(value)
  }

  function toggleRateAmount(ruleId: string) {
    const rule = props.rules.find((rule) => rule.rule_id === ruleId)
    const value = rule?.rate_amount_type === 'fee' ? 'subsidy' : 'fee'
    handleChangeRule(ruleId, 'rate_amount_type')(value)
  }

  const items = props.rules.map(
    (rule: IPolicyRuleTripTaxation, idx): Row => [
      props.validation[idx],
      rule.rule_id,
      rule.name,
      rule.vehicle_types,
      rule.geographies,
      rule.rate_amount,
      rule.rate_amount_type,
      rule.proration_kind,
      rule.proration_areas_mapping,
      rule.proration_time_spent_report_area,
    ]
  )
  return (
    <SimpleTable
      headerRow={HEADERS.map((title, index) => (
        <TableCell key={`key-${title}-${index}`}>
          <FieldTitle title={title} />
        </TableCell>
      ))}
      items={items}
      rowRenderer={([
        ruleValidation,
        ruleId,
        name,
        vehicleTypes,
        geographies,
        rate_amount,
        rate_amount_type,
        prorationKind,
        prorationAreasMapping,
        prorationTimeSpentReportArea,
      ]: Row) => (
        <>
          <TableCell style={{ verticalAlign: 'middle', paddingTop: '2%' }} align="left" padding={'none'}>
            <Can I={DomainPermission_VerbEnum.write} a={DomainPermission_DomainEnum.fee_rates}>
              {props.handleDelete && (
                <DeleteRuleButton
                  label={t('Delete rule')}
                  disabled={props.disabled}
                  onClick={props.handleDelete(ruleId)}
                />
              )}
            </Can>
          </TableCell>

          <TableCell id="fee_rule_field_name" style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
            <RuleNameField
              value={name}
              handleChange={handleChangeRule(ruleId, 'name')}
              disabled={props.disabled}
              error={!ruleValidation.isFieldValid('name')}
              helperText={ruleValidation.getFieldErrorMsg('name')}
            />
          </TableCell>

          <TableCell
            id="fee_rule_field_vehicle_type"
            style={{ height: '100px', verticalAlign: 'bottom' }}
            align="justify"
          >
            <VehicleTypesField
              value={vehicleTypes}
              handleChange={handleChangeRule(ruleId, 'vehicle_types')}
              disabled={props.disabled}
              error={!ruleValidation.isFieldValid('vehicle_types')}
              helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
            />
          </TableCell>

          <TableCell
            id="fee_rule_field_geography"
            align="justify"
            style={{
              maxWidth: '400px',
              height: '100px',
              verticalAlign: 'bottom',
            }}
          >
            <GeographiesField
              value={geographies}
              allGeographies={props.allGeographies}
              handleChange={handleChangeRule(ruleId, 'geographies')}
              disabled={props.disabled}
              error={!ruleValidation.isFieldValid('geographies')}
              helperText={ruleValidation.getFieldErrorMsg('geographies')}
            />
          </TableCell>

          <TableCell id="fee_rule_field_fees" style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
            <FieldTitle title={upperFirst(amountTitles[rate_amount_type])} />
            <NumberField
              value={rate_amount}
              handleChange={(value) => handleRateAmountChange(ruleId, value)}
              disabled={props.disabled}
              inputType={'text'}
            />
          </TableCell>

          <TableCell id="fee_rule_toggle_fee">
            <IconButton onClick={() => toggleRateAmount(ruleId)} disabled={props.disabled}>
              {rate_amount_type === 'fee' ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </TableCell>

          <TableCell id="fee_rule_field_proration" style={{ height: '100px', maxWidth: '200px' }} align="right">
            <ProrationField
              disabled={props.disabled}
              prorationKind={prorationKind}
              prorationAreasMapping={prorationAreasMapping}
              areas={props.allGeographies.filter((areaLabelResponse) => geographies.includes(areaLabelResponse.id))}
              prorationTimeSpentReportArea={prorationTimeSpentReportArea}
              handleProrationChange={handleChangeRule(ruleId)}
            />
          </TableCell>
        </>
      )}
    />
  )
}

export default TripTaxationRule
